import BuyPermitButton from 'components/park/detail/BuyPermitButton';
import EditPermitButton from 'components/park/detail/EditPermitButton';
import ParkingHereButton from 'components/park/timeSelection/ParkingHereButton';
import ParkingContext from 'context/ParkingContext';
import useIsDesktop from 'hooks/useIsDesktop';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import React, { useContext } from 'react';
import { StyleSheet, ViewProps } from 'react-native';
import { Shadow } from 'react-native-shadow-2';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { ParkingLayoutProps } from 'types/common';
import { isWeb } from 'utils/deviceUtils';
import { doAllPermitsHaveFutureStartDates } from 'utils/permitUtils';
import EnableAnprButton from '../detail/EnableAnprButton';
import PrepaidActiveButton from '../detail/PrepaidActiveButton';
import StartParkingButton from '../detail/StartParkingButton';
import StartParkAndChargeButton from '../evcharge/StartParkAndChargeButton';
import ChargingHereButton from '../evcharge/detail/ChargingHereButton';
import StartChargingButton from '../evcharge/detail/StartChargingButton';

const Empty = () => <></>;

const styles = StyleSheet.create({
  shadow: {
    width: '100%',
  },
});

type ParkZoneDetailsComponentProps = {
  isAnpr: boolean;
  showChargeButton: boolean;
  showParkAndChargeButton: boolean;
  hasPrepaidForSelectedVehicle: boolean;
  hasSalesOrderForSelectedVehicle: boolean;
  userPaysParking: boolean;
  areAllPermitsFutureDated: boolean;
};

const ParkZoneDetailsComponent = ({
  isAnpr,
  showChargeButton,
  showParkAndChargeButton,
  hasPrepaidForSelectedVehicle,
  hasSalesOrderForSelectedVehicle,
  userPaysParking,
  areAllPermitsFutureDated,
}: ParkZoneDetailsComponentProps) => {
  if (isAnpr && !showChargeButton) {
    return <EnableAnprButton />;
  } else if (showParkAndChargeButton) {
    return <StartParkAndChargeButton />;
  } else if (showChargeButton) {
    return <StartChargingButton />;
  } else if (
    hasPrepaidForSelectedVehicle ||
    (hasSalesOrderForSelectedVehicle &&
      !userPaysParking &&
      !areAllPermitsFutureDated)
  ) {
    return (
      <PrepaidActiveButton
        permitActive={hasSalesOrderForSelectedVehicle && !userPaysParking}
      />
    );
  } else {
    return <StartParkingButton />;
  }
};

const ParkingWizardFooterSwitch = ({
  currentScreen,
}: ParkingLayoutProps & ViewProps) => {
  const { parkingZoneResult, salesOrdersForZoneAndVehicle } =
    useContext(ParkingContext);
  const parkingZoneType = useActiveElementStore(
    (state) => state.activeElementType
  );
  const isAnpr =
    parkingZoneResult?.data?.parkZone?.parkingMethods?.anpr === true;
  const selectedVehicle = useSelectedParkingVehicle();
  const { hasPrepaidForSelectedVehicle } = useReadPurchasedPrepaidTickets({
    selectedVehicle: selectedVehicle,
  });

  const areAllPermitsFutureDated = doAllPermitsHaveFutureStartDates(
    salesOrdersForZoneAndVehicle
  );

  const isDesktop = useIsDesktop();

  const selectedTab = useParkzoneStore((state) => state.selectedParkingZoneTab);
  const showChargeButton =
    parkingZoneType === 'CHARGING' ||
    (parkingZoneType === 'PARK_AND_CHARGE' && selectedTab === 'CHARGE');
  const showParkAndChargeButton =
    parkingZoneType === 'PARK_AND_CHARGE' && selectedTab === 'PARK_AND_CHARGE';

  let Component: React.FC<any>;
  let shadowDistance;
  const defaultShadowDistance = isWeb && isDesktop ? 0 : 10;

  const hasSalesOrder = !!salesOrdersForZoneAndVehicle?.length;
  const componentProps = {
    isAnpr,
    showChargeButton,
    showParkAndChargeButton,
    hasPrepaidForSelectedVehicle: hasPrepaidForSelectedVehicle.length > 0,
    hasSalesOrderForSelectedVehicle: hasSalesOrder,
    userPaysParking: hasSalesOrder
      ? salesOrdersForZoneAndVehicle?.some(
          (salesOrder) =>
            salesOrder.orderLines?.[0]?.pmc?.doShortTermPriceCalculation
        )
      : true,
    areAllPermitsFutureDated,
  };

  switch (currentScreen) {
    case 'SHOW_DETAILS':
    case 'CHOOSE_END_TIME':
      shadowDistance = defaultShadowDistance;
      Component = ParkZoneDetailsComponent;
      break;
    case 'INFO':
      shadowDistance = defaultShadowDistance;
      Component = showChargeButton ? ChargingHereButton : ParkingHereButton;
      break;
    case 'RESUME_PARKING':
      shadowDistance = defaultShadowDistance;
      Component = StartParkingButton;
      break;
    case 'PURCHASE_PREPAID':
      shadowDistance = defaultShadowDistance;
      Component = BuyPermitButton;
      break;
    case 'EDIT_PREPAID':
      shadowDistance = defaultShadowDistance;
      Component = EditPermitButton;
      break;
    case 'SELECT_CHARGER':
      shadowDistance = defaultShadowDistance;
      Component = showChargeButton
        ? StartChargingButton
        : StartParkAndChargeButton;
      break;
    case 'SEARCH':
    default:
      shadowDistance = 0;
      Component = Empty;
      break;
  }

  return (
    <Shadow
      sides={{ top: true, bottom: false, end: false, start: false }}
      distance={shadowDistance}
      style={styles.shadow}
      containerStyle={styles.shadow}>
      <Component {...componentProps} currentScreen={currentScreen} />
    </Shadow>
  );
};

export default ParkingWizardFooterSwitch;
